import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Layout } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should an interior designer invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'An interior designer invoice should include your name or firm name, the client name and project address, a unique invoice number and project phase, payment due date, a description of services (concept design, 3D rendering, procurement, site visits), design fees, any markup on furniture and materials if applicable, applicable tax, and your payment details.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do interior designers charge clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Interior designers typically use one of three models: an hourly rate for consultations and design work; a flat project fee for the full design scope; or a percentage of the total project cost (common for large renovations). Many designers also charge a markup (10–30%) on furniture and materials they procure. Define your fee structure clearly in your contract and reflect it accurately on your invoice.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should interior designers invoice for procurement separately?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Keep procurement invoices separate from design service invoices. When purchasing furniture, fixtures, or materials on behalf of a client, create a separate invoice or purchase order for each supplier payment with a clear markup disclosed. Mixing design fees with procurement costs in a single line item creates confusion and makes it harder to manage project budgets.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do interior designers invoice for project phases?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Phase-based billing is standard. Create a separate invoice for each project stage — Discovery & Concept, Design Development, Documentation, and Installation. Tie each invoice to agreed deliverables and milestones. This reduces your payment risk and gives clients clear checkpoints. Reference the phase name and agreed fee from your contract on each invoice.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this interior designer invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free interior designer invoice template. Fill in your project details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function InteriorDesignerInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Interior Designer Invoice Template: Free PDF for UK & US Design Professionals | Invilic"
        description="Free interior designer invoice template for UK and US design professionals. Phase-based billing, procurement invoicing, VAT-ready. Instant PDF. No sign-up needed."
        canonical="https://www.invilic.com/interior-designer-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Interior Designer Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for interior designers and decorators. Bill for design phases, consultations, and procurement. Download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-rose-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Design Professional
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Residential interior designers', desc: 'Bill homeowners for concept design, furniture selection, and full project management.' },
              { title: 'Commercial & hospitality designers', desc: 'Invoice for office, retail, and hospitality fit-outs with phase-based milestone billing.' },
              { title: 'Online & e-design consultants', desc: 'Bill for virtual design packages, mood boards, shopping lists, and design guides.' },
              { title: 'Interior decorators', desc: 'Issue invoices for styling consultations, space planning, and decorating services.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center flex-shrink-0">
                  <Layout className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Interior Design Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Designer / firm name and logo',
              'Client name and project address',
              'Invoice number and project phase',
              'Payment due date',
              'Design services per line item',
              'Hourly rate or phase fee',
              'Procurement and materials (marked up)',
              'Site visit and travel charges',
              'Concept, rendering or 3D fees',
              'Subtotal, tax and discount',
              'Total amount due',
              'Bank details or payment link',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-rose-100 dark:border-rose-800/30 bg-rose-50 dark:bg-rose-900/10">
                <CheckCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your Design Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Add your project details', desc: 'Enter your services, phase, rate, and any procurement or expenses.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to send to your client.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-rose-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Interior Designer Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* UK VAT section for interior designers */}
        <section className="mb-16 p-6 rounded-2xl border border-rose-100 dark:border-rose-800/40 bg-rose-50 dark:bg-rose-900/20">
          <h2 className="font-bold text-gray-900 dark:text-white mb-3">Interior Designer Invoicing in the UK — VAT & HMRC Rules</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            UK interior designers should be aware of these tax and invoicing requirements:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { label: 'VAT on design services: 20%', detail: 'Interior design services are standard-rated at 20% VAT. Register with HMRC once turnover exceeds £90,000 in any 12-month period.' },
              { label: 'VAT on construction work', detail: 'Some construction work is reduced-rated (5%) or zero-rated. If you project manage construction, check HMRC VAT Notice 708 for the applicable rate.' },
              { label: 'Procurement invoicing', detail: 'When purchasing furniture and materials for clients, issue a separate procurement invoice showing cost price + your markup + VAT. Disclose the markup percentage as agreed in your contract.' },
              { label: 'HMRC Self Assessment', detail: 'Sole trader designers earning over £1,000 must register for Self Assessment. Limited company designers pay corporation tax and submit annual accounts to Companies House.' },
            ].map(({ label, detail }) => (
              <div key={label} className="p-3 bg-white dark:bg-gray-900/50 rounded-xl border border-rose-100 dark:border-rose-800/30">
                <p className="font-semibold text-gray-900 dark:text-white text-xs mb-1">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Design Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-rose-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Interior Designer Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
